/**
 * `encode/gzip` — text ↔ gzip+base64 codec.
 *
 * Backed by the browser's native `CompressionStream` /
 * `DecompressionStream` APIs (Chrome 80+, Firefox 113+, Safari 16.4+).
 * No JS-side gzip implementation — if the browser doesn't ship the
 * Web Stream codec we throw a clear, actionable error rather than
 * silently dragging in pako (~45 KB) for a tiny minority of users.
 *
 * Input cap: 5 MiB. Beyond that the page UI gets sluggish and the
 * base64 output is no longer pleasant to copy/paste; users with
 * larger payloads should use a real gzip CLI.
 */

export const GZIP_INPUT_CAP = 5 * 1024 * 1024;

export interface GzipResult {
  base64: string;
  /** Output bytes / input bytes. < 1 means compression saved space. */
  ratio: number;
  /** Input size in bytes (UTF-8 encoded). */
  in: number;
  /** Output size in bytes (raw gzip stream, before base64). */
  out: number;
}

function checkCompressionStreamAvailable(): void {
  if (typeof CompressionStream === 'undefined') {
    throw new Error(
      '此浏览器不支持 CompressionStream，请升级到 Chrome 80+ / Firefox 113+ / Safari 16.4+',
    );
  }
}

function checkDecompressionStreamAvailable(): void {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error(
      '此浏览器不支持 DecompressionStream，请升级到 Chrome 80+ / Firefox 113+ / Safari 16.4+',
    );
  }
}

async function readAllChunks(stream: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      total += value.byteLength;
    }
  }
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  // Build a binary string in 32 KiB chunks to avoid the
  // String.fromCharCode arg-length cap.
  const CHUNK = 0x8000;
  let bin = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + CHUNK)),
    );
  }
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  // Strip data: URI prefix and whitespace defensively.
  const cleaned = b64.replace(/^data:[^;,]*;base64,/, '').replace(/\s+/g, '');
  if (!cleaned) return new Uint8Array(0);
  let bin: string;
  try {
    bin = atob(cleaned);
  } catch {
    throw new Error('输入不是有效的 base64 字符串');
  }
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function gzipToBase64(text: string): Promise<GzipResult> {
  checkCompressionStreamAvailable();

  const inBytes = new TextEncoder().encode(text);
  if (inBytes.byteLength > GZIP_INPUT_CAP) {
    throw new Error(
      `输入超过 5 MiB 限制（当前 ${(inBytes.byteLength / 1024 / 1024).toFixed(2)} MiB）`,
    );
  }

  const stream = new Blob([inBytes as BlobPart]).stream().pipeThrough(
    new CompressionStream('gzip'),
  );
  const compressed = await readAllChunks(stream);
  return {
    base64: bytesToBase64(compressed),
    ratio: inBytes.byteLength === 0 ? 0 : compressed.byteLength / inBytes.byteLength,
    in: inBytes.byteLength,
    out: compressed.byteLength,
  };
}

export async function base64ToGzipString(b64: string): Promise<string> {
  checkDecompressionStreamAvailable();

  const bytes = base64ToBytes(b64);
  if (bytes.byteLength === 0) return '';

  let decompressed: Uint8Array;
  try {
    const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(
      new DecompressionStream('gzip'),
    );
    decompressed = await readAllChunks(stream);
  } catch (e) {
    throw new Error(
      `解压失败：${e instanceof Error ? e.message : String(e)}（输入可能不是 gzip 流）`,
    );
  }
  return new TextDecoder('utf-8', { fatal: false }).decode(decompressed);
}
